import { type Models, select } from '@ps/web-core-store';
import { dateFormat, getIntlDateFormat, pickerFormat } from '@ps/web-core-utils';
import { Label } from '@ps/web-shared-ui';
import type { ValidatorTranslation } from '@ps/web-shared-validators';
import classNames from 'clsx';
import type flatpickr from 'flatpickr';
import { isArray } from 'lodash';
import type { Ref } from 'react';
import type React from 'react';
import { useMemo } from 'react';
import type { FieldRenderProps } from 'react-final-form';
import { Field } from 'react-final-form';
import Flatpickr from 'react-flatpickr';
import { type DateTimePickerHandle } from 'react-flatpickr';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CalendarIcon } from '../../../assets/icons';
import Translation from '../../Translation/Translation';
import type { InjectedValidatorProps } from '../../hoc';
import withValidator from '../../hoc/withValidator';
import { usePauseValidator } from '../../hooks';
import { mapDatePickerLocale } from '../../locales/locales';
import 'flatpickr/dist/l10n';

import styles from './StarsDatePicker.module.scss';
import { handleUsingParseIso } from './utils';

export type StarsDatePickerProps = flatpickr.Options.Options & {
  id: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  icon?: React.ReactElement;
  hideLabel?: boolean;
  showIcon?: boolean;
  hideDateFormat?: boolean;
  customContainerClassName?: string;
  labelClassName?: string;
  locale?: Models['locale']['state'];
  pickerRef?: Ref<DateTimePickerHandle | undefined>;
  placeholder?: string;
  isIsoParsed?: boolean;
};

type CalendarControlsProps = StarsDatePickerProps &
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FieldRenderProps<any, HTMLInputElement>;

type OwnProps = StarsDatePickerProps & InjectedValidatorProps;
export type Props = OwnProps;

const CalendarControls: React.FC<CalendarControlsProps> = (props) => {
  const {
    id,
    label,
    hideLabel,
    disabled,
    className,
    meta: { invalid, dirty, valid, dirtySinceLastSubmit, submitError, error, touched },
    input,
    icon,
    pickerRef,
    showIcon = true,
    hideDateFormat = false,
    customContainerClassName,
    labelClassName,
    placeholder,
    isIsoParsed = true
  } = props;

  const onChange: flatpickr.Options.Hook = (_, selectedDate) => {
    input.onChange(selectedDate);
  };

  const { formatDateToParts } = useIntl();
  const dateParts = formatDateToParts(new Date());

  const datePickerFormat = useMemo(
    () => getIntlDateFormat(dateParts, pickerFormat, true),
    [dateParts]
  );

  const textFormat = useMemo(() => getIntlDateFormat(dateParts, dateFormat), [dateParts]);

  const options: flatpickr.Options.Options = {
    altInput: true,
    altFormat: datePickerFormat,
    altInputClass: styles.input,
    disableMobile: true,
    defaultDate: handleUsingParseIso(isIsoParsed, input.value),
    static: true,
    onChange,
    ...props,
    locale: mapDatePickerLocale(props.locale) as flatpickr.CustomLocale
  };

  const Icon = icon || <CalendarIcon className={styles.icon} />;

  const getErrorMessageText = (errorMessage: ValidatorTranslation | ValidatorTranslation[]) =>
    isArray(errorMessage) ? (
      <p>
        {errorMessage.map((item, index) => (
          <>
            <Translation key={index} {...item} />{' '}
          </>
        ))}
      </p>
    ) : (
      <Translation tagName="p" {...errorMessage} />
    );

  const hasError = invalid && (dirty || touched) && !disabled;
  const isValid = !disabled && (valid || dirtySinceLastSubmit);

  return (
    <div
      className={classNames(styles.container, {
        [styles.error]: hasError,
        [styles.valid]: isValid,
        [styles.disabled]: disabled,
        [customContainerClassName as string]: customContainerClassName && !hasError && !isValid
      })}
    >
      <Label id={id} hidden={hideLabel} className={labelClassName}>
        {label}
        {!hideDateFormat && (
          <span className={styles.textFormat} suppressHydrationWarning>
            {textFormat}
          </span>
        )}
      </Label>
      <div className={classNames({ [styles.calendar]: !disabled }, className)}>
        <Flatpickr
          ref={pickerRef}
          options={options}
          id={id}
          data-testid={id}
          placeholder={placeholder}
          type="hidden"
          value={handleUsingParseIso(isIsoParsed, input.value)}
        />
        {showIcon && !disabled && Icon}
      </div>
      {submitError ? (
        <p>{submitError}</p>
      ) : (
        invalid && (dirty || touched) && getErrorMessageText(error.message)
      )}
    </div>
  );
};

const StarsDatePicker: React.FC<Props> = ({
  validator,
  validationErrors,
  defaultDate,
  ...rest
}) => {
  const locale = useSelector(select.locale.get);
  usePauseValidator();

  // TODO: make CalendarControls return type compatible with component prop
  return (
    <Field
      {...rest}
      locale={locale}
      name={rest.name || rest.id}
      component={CalendarControls as unknown as 'input'}
      validate={validator}
      initialValue={defaultDate}
    />
  );
};

export default withValidator(StarsDatePicker);
